import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  LexicalTypeaheadMenuPlugin,
  useBasicTypeaheadTriggerMatch,
} from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLexicalContext } from "../../lexical-context";
import { $createChatReferenceChannelNodeMui } from "../../nodes/channel/channel-node-mui";
import { ChannelTypeaheadOption } from "../../types/channel-typeahead-option";
import { TypeaheadOptionsList } from "./typeahead-option-list";

import type { TextNode } from "lexical";
import type { FC } from "react";

const useChannelLookupService = (searchText: string | null): ChannelTypeaheadOption[] => {
  const { chatChannelRecipients } = useLexicalContext();

  return useMemo(() => {
    if (searchText === null || !chatChannelRecipients.data) {
      return [];
    }
    // We can only search for channels with a display name.
    return chatChannelRecipients.data
      .filter((r) => r.display?.toLowerCase().includes(searchText.toLowerCase()))
      .map((result) => new ChannelTypeaheadOption(result.channel_id, result.display!));
  }, [searchText, chatChannelRecipients.data]);
};

export const ChannelTypeaheadPlugin: FC = () => {
  const [queryString, setQueryString] = useState<string | null>(null);
  const options = useChannelLookupService(queryString);
  const [editor] = useLexicalComposerContext();
  const onSelectOption = useCallback(
    (selected: ChannelTypeaheadOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        const chatReferenceChannelNode = $createChatReferenceChannelNodeMui(selected.channel_id);
        if (nodeToReplace) {
          // nodeToReplace is the text between the # and the cursor.
          // the next sibling will be either the rest of the single channel name (e.g. if the whole channel was pasted in)
          // or will be the rest of the sentence.
          // If the nodeToReplace appended to the next sibling equals the selected channel then replace both nodes.
          const nextSibling = nodeToReplace.getNextSibling();
          if (
            nextSibling &&
            nodeToReplace.getTextContent() + nextSibling.getTextContent() === `#${selected.display}`
          ) {
            nextSibling.remove();
          }

          nodeToReplace.replace(chatReferenceChannelNode);
        }
        closeMenu();
      });
    },
    [editor],
  );

  const triggerFn = useBasicTypeaheadTriggerMatch("#", {
    minLength: 1,
    // copied from node_modules/.pnpm/...@lexical/react/LexicalTypeaheadMenuPlugin.dev.js
    // modified to allow the dash for slack.
    punctuation: "\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\[\\]\\\\/!%'\"~=<>_:;",
  });

  return (
    <LexicalTypeaheadMenuPlugin<ChannelTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      options={options}
      triggerFn={triggerFn}
      menuRenderFn={(anchorElementRef, renderProps) =>
        anchorElementRef.current
          ? createPortal(
              renderProps.options.length ? (
                <TypeaheadOptionsList
                  data-testid="channel-typeahead"
                  {...renderProps}
                  options={options}
                  renderOption={(option) => <span>{option.display}</span>}
                  sx={{
                    marginTop: 0,
                    minWidth: 240,
                  }}
                />
              ) : null,
              anchorElementRef.current,
            )
          : null
      }
    />
  );
};
